/** Only show group-style seeds (A1, B2) - hide numeric bracket seeds (1, 2, 8) which are confusing */
const isGroupSeed = (s) => s && /^[A-Za-z]\d+$/.test(s);

/**
 * Get group seed labels (A1, B2) for teams in a knockout match from bracket_position.
 * For QF/R16 matches, parses bracket_position directly.
 * For SF/Final, traces back through previous rounds to find the original group seed.
 * Returns null for numeric seeds (1, 2, 8) - only group seeds (A1, B2) are shown to avoid confusion.
 */
export const getTeamSeedsForMatch = (match, allMatches) => {
  const bp = match?.bracket_position;
  if (!bp || !bp.includes('_vs_')) return [null, null];
  const parts = bp.split('_vs_');
  const pos1 = parts[0]?.trim();
  const pos2 = parts[1]?.trim();
  if (!pos1 || !pos2) return [null, null];
  if (!pos1.endsWith('_winner') && !pos2.endsWith('_winner')) {
    return [isGroupSeed(pos1) ? pos1 : null, isGroupSeed(pos2) ? pos2 : null];
  }
  const knockoutMatches = (allMatches || []).filter(m => m.round !== 'group');
  const resolveSlot = (slot) => {
    const m = slot.match(/^(QF|SF|R16)(\d+)_winner$/);
    if (!m) return null;
    const [, roundPrefix, numStr] = m;
    const roundMap = { QF: 'quarter_final', SF: 'semi_final', R16: 'round_of_16' };
    const round = roundMap[roundPrefix];
    const matchOrder = parseInt(numStr, 10);
    const prevMatch = knockoutMatches.find(m => m.round === round && (m.match_order ?? 0) === matchOrder);
    if (!prevMatch) return null;
    const prevParts = prevMatch.bracket_position?.split('_vs_');
    if (!prevParts) return null;
    const prevPos1 = prevParts[0]?.trim();
    const prevPos2 = prevParts[1]?.trim();
    if (!prevPos1?.endsWith('_winner') && !prevPos2?.endsWith('_winner')) {
      if (!prevMatch.winner_entry_id) return null;
      const winnerIsTeam1 = prevMatch.winner_entry_id === prevMatch.entry1_id;
      return winnerIsTeam1 ? prevPos1 : prevPos2;
    }
    if (!prevMatch.winner_entry_id) return null;
    const winnerIsTeam1 = prevMatch.winner_entry_id === prevMatch.entry1_id;
    return resolveSlot(winnerIsTeam1 ? prevPos1 : prevPos2);
  };
  const s1 = pos1.endsWith('_winner') ? resolveSlot(pos1) : pos1;
  const s2 = pos2.endsWith('_winner') ? resolveSlot(pos2) : pos2;
  return [isGroupSeed(s1) ? s1 : null, isGroupSeed(s2) ? s2 : null];
};
